import React from "react";
import { methodDisplayName, methodIconClass, METHOD_TYPE } from "./loginVerifyHelpers";
import "./loginVerifyFlow.css";

/**
 * Screen 1 — pick a security method and tap Verify.
 * Completed methods show a green check and disabled control.
 */
export default function LoginMethodSelectPanel({
  methods = [],
  completedTypes = [],
  verificationMode = "",
  busyType = null,
  onVerifyMethod,
  onContinue,
  continueDisabled = true,
  continueLabel = "Continue",
  recoverableMethods = [],
  recoveryBusy = false,
  onUnableToVerify,
}) {
  const isAllRequired = verificationMode === "ALL_REQUIRED";
  const subtitle = isAllRequired
    ? "New device: verify every method below to finish sign-in."
    : "Choose any one method to verify your identity.";

  return (
    <div className="lmv-panel lmv-panel--select">
      <h1 className="login-wizard-title login-wizard-title--verify">Verify your identity</h1>
      <p className="signup-wizard-subtitle">{subtitle}</p>

      <ul className="lmv-method-list" role="list">
        {methods.map((method) => {
          const type = Number(method.type);
          const done = completedTypes.includes(type);
          const busy = busyType === type;
          const label = method.label || methodDisplayName(type);
          const detail =
            method.maskedValue ||
            (type === METHOD_TYPE.AUTHENTICATOR
              ? "Use your authenticator app"
              : type === METHOD_TYPE.PASSKEY
                ? "Face ID, Touch ID, or Windows Hello"
                : method.description || "");

          return (
            <li key={type} className={`lmv-method-row${done ? " is-done" : ""}`}>
              <div className="lmv-method-row__main">
                <span className="lmv-method-row__icon" aria-hidden>
                  <i className={method.icon || methodIconClass(type)} />
                </span>
                <div className="lmv-method-row__text">
                  <strong>{label}</strong>
                  {detail ? <span className="lmv-method-row__detail">{detail}</span> : null}
                </div>
              </div>
              {done ? (
                <span className="lmv-check" aria-label={`${label} verified`}>
                  <i className="ri-check-line" aria-hidden />
                </span>
              ) : (
                <button
                  type="button"
                  className="lmv-verify-btn"
                  disabled={busy || busyType != null}
                  aria-busy={busy}
                  onClick={() => onVerifyMethod?.(method)}
                >
                  {busy ? "…" : "Verify"}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {typeof onContinue === "function" ? (
        <button
          type="button"
          className="login-wizard-next-btn lmv-continue-btn"
          disabled={continueDisabled}
          onClick={() => onContinue()}
        >
          {continueLabel}
        </button>
      ) : null}

      {recoverableMethods.length > 0 && typeof onUnableToVerify === "function" ? (
        <div className="lmv-recovery-links">
          <button type="button" className="lmv-cant-access" disabled={recoveryBusy} onClick={onUnableToVerify}>
            Unable to verify?
          </button>
        </div>
      ) : null}
    </div>
  );
}
